import { hexToHSL } from './colorUtils';

/**
 * Generates brand color variations from HSL values
 * @param {Object} hslValues - HSL color object { h, s, l }
 * @returns {Object} Brand color variations { brand50, brand40, brand10 }
 * @throws {Error} If HSL values are invalid
 */
export const generateBrandColorVariations = (hslValues) => {
  // Validate HSL values are valid numbers
  if (
    typeof hslValues.h !== 'number' || isNaN(hslValues.h) ||
    typeof hslValues.s !== 'number' || isNaN(hslValues.s) ||
    typeof hslValues.l !== 'number' || isNaN(hslValues.l)
  ) {
    throw new Error('Invalid HSL values returned from hexToHSL');
  }

  // Brand-50: main brand color (the brandColor itself)
  const brand50 = `hsl(${hslValues.h}, ${hslValues.s}%, ${hslValues.l}%)`;

  // Brand-40: lighter version (similar to accent-40: s=56%, b=88% in HSB)
  // In HSL terms: reduce saturation slightly, increase lightness significantly
  const brand40Saturation = Math.max(30, hslValues.s * 0.6); // Reduce to ~60% of original, min 30%
  const brand40Lightness = Math.min(88, hslValues.l + (88 - hslValues.l) * 0.7); // Move toward 88%
  const brand40 = `hsl(${hslValues.h}, ${brand40Saturation}%, ${brand40Lightness}%)`;

  // Brand-10: very light tint (low saturation ~1%, high lightness ~93%)
  const brand10 = `hsl(${hslValues.h}, 1%, 93%)`;

  return { brand50, brand40, brand10 };
};

/**
 * Calculates heart layers for the fallback header image
 * Generates an array of heart objects with progressive scales and lighter colors
 * @param {Object} hslValues - HSL color object { h, s, l }
 * @param {string} brand50 - The main brand color (HSL string)
 * @returns {Array} Array of heart objects with { scale, color, opacity }
 */
export const calculateHeartLayers = (hslValues, brand50) => {
  // Calculate how many hearts we need to cover the canvas
  // Canvas is 1600x900, diagonal is ~1838px
  // Base heart is roughly 570px wide (from x=575 to x=1145) and 489px tall (from y=255 to y=744)
  // To cover the canvas, we need the largest heart to cover the diagonal
  // Heart center is at (800, 450), so we need to cover from center to corners
  const canvasWidth = 1600;
  const canvasHeight = 900;
  const centerX = 800;
  const centerY = 450;
  
  // Distance from center to farthest corner
  const maxDistance = Math.max(
    Math.sqrt(centerX * centerX + centerY * centerY), // top-left
    Math.sqrt((canvasWidth - centerX) * (canvasWidth - centerX) + centerY * centerY), // top-right
    Math.sqrt(centerX * centerX + (canvasHeight - centerY) * (canvasHeight - centerY)), // bottom-left
    Math.sqrt((canvasWidth - centerX) * (canvasWidth - centerX) + (canvasHeight - centerY) * (canvasHeight - centerY)) // bottom-right
  ); // ~900px

  // Base heart extends roughly 285px from center horizontally and 245px vertically
  // We need to scale until the heart covers maxDistance
  const baseHeartRadius = Math.max(285, 245); // ~285px
  const maxScale = (maxDistance / baseHeartRadius) * 1.5; // Add 50% buffer to ensure full coverage

  // Generate hearts with progressive scales and lighter colors
  const hearts = [];
  let scale = 1.0;
  let heartIndex = 0;
  const scaleIncrement = 0.25; // Smaller increment for smoother coverage
  const totalHearts = Math.ceil((maxScale - 1.0) / scaleIncrement) + 1; // +1 for the center heart

  while (scale <= maxScale) {
    // First heart (scale 1.0) MUST use full brand-50 color - no calculations
    const heartColor = scale === 1.0
      ? brand50
      : (() => {
          // Calculate progressively lighter color for larger hearts
          // Progress from 0 (first heart after center) to 1 (outermost heart)
          const progress = (heartIndex - 1) / Math.max(1, totalHearts - 1); // Normalize to 0-1, excluding center heart
          const saturation = Math.max(1, hslValues.s * (1 - progress * 0.95)); // Reduce to ~5% of original
          const lightness = Math.min(93, hslValues.l + (93 - hslValues.l) * progress * 0.9); // Increase toward 93%
          return `hsl(${hslValues.h}, ${saturation}%, ${lightness}%)`;
        })();

    hearts.push({
      scale,
      color: heartColor,
      opacity: heartIndex === 0 ? 1 : Math.max(0.7, 1 - ((heartIndex - 1) / Math.max(1, totalHearts - 1)) * 0.2), // Slightly fade larger hearts
    });

    // Increase scale for next heart
    scale += scaleIncrement;
    heartIndex++;
  }

  return hearts;
};

/**
 * Generates all data needed for dynamic SVG fallback image
 * @param {string} brandColor - Hex color string (e.g., "#FF0000")
 * @returns {Object} SVG data { brand50, brand40, brand10, hearts }
 * @throws {Error} If brandColor is invalid
 */
export const generateFallbackImageData = (brandColor) => {
  const hslValues = hexToHSL(brandColor);
  const colorVariations = generateBrandColorVariations(hslValues);
  const hearts = calculateHeartLayers(hslValues, colorVariations.brand50);

  return {
    ...colorVariations,
    hearts,
  };
};

